import { useCallback, useRef } from 'react';
import { useNavigate, useMatch, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_WORKLIST_ITEM, GET_LAST_VIEWED_WORKLIST_ITEM_SMIDS } from 'modules/Apollo/queries';
import type {
  GetWorklistItemQuery,
  GetWorklistItemQueryVariables,
  GetLastViewedWorklistItemSmidsQuery,
  GetLastViewedWorklistItemSmidsQueryVariables,
} from 'generated/graphql';
import { AppRoutePath, NOT_FOUND_CASE_ID } from 'config/constants';
import { useAsyncGlobalAction } from 'hooks/useGlobalAction';
import { useOpenTabs } from './useOpenTabs';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import usePrevious from 'react-use/esm/usePrevious';
import { useUrlBuilder } from './useUrlBuilder';
import { PRIOR_STUDIES_PARAM, EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM } from './studyParams';
import { clearProviderMap } from 'modules/viewer/workers/PixelDataSharedWorker';
import { saveWindowSyncInfo } from 'modules/windowDesyncDetector';
import { logger } from 'modules/logger';
import { useStudies } from '../domains/viewer/Viewer/StudyLoader/useStudies';
import { getWARDCaseId } from 'domains/viewer/Viewer/Ward/utils';
import { WithApolloContext } from 'modules/Apollo/types';
import { useViewerMatch } from 'utils/viewerUrlUtils';

type HookResult = {
  currentCaseId: Nullable<string>;
  /** Current case/worklist item in context */
  currentCase: Nullable<GetWorklistItemQuery['worklistItem']>;
  /** Refetches the case globally across all windows */
  refreshCase: () => Promise<void>;
  /** Loading state for case */
  loadingCase: boolean;
};

// VX-4394: order-insensitive URL compare for the same-URL navigate guard below. The same query
// params can arrive in a different order (e.g. `hp` added before `prior_studies` on an earlier
// navigation), and a raw string compare would let a semantically-identical URL slip a redundant
// navigate through. Compare path + sorted params so order doesn't matter.
function normalizeUrlForCompare(url: string): string {
  const q = url.indexOf('?');
  if (q === -1) return url;
  const params = new URLSearchParams(url.slice(q + 1));
  params.sort();
  // Omit the `?` when there are no params, so a bare-`?` URL (e.g. `/case/x/viewer/0?`) normalizes
  // to the same string as one with no query at all — otherwise the same-URL guard would miss.
  const qs = params.toString();
  return qs.length > 0 ? `${url.slice(0, q)}?${qs}` : url.slice(0, q);
}

export const useCurrentComparativeStudies = ({
  context,
}: WithApolloContext): [
  ReadonlyArray<string>,
  (arg1: { caseSmid?: string; studySmids: ReadonlyArray<string> }) => unknown,
] => {
  const currentCaseId = useCurrentCaseId();
  const { priorStudiesIds, autoloadedStudiesIds } = useStudies({ context });

  const buildUrl = useUrlBuilder();
  const navigate = useNavigate();
  const previousCaseId = usePrevious(currentCaseId);
  const [shouldRefreshViewer] = useFeatureFlagEnabled(FF.refresh_viewer_on_new_case);
  const matchViewer = useMatch(AppRoutePath.VIEWER);
  const [searchParams] = useSearchParams();

  const setCurrentComparativeStudies = useCallback(
    ({
      caseSmid = currentCaseId,
      studySmids,
    }: {
      caseSmid?: Nullable<string>;
      studySmids: ReadonlyArray<string>;
    }) => {
      // VX-4394: bail out for a missing or NOT_FOUND case. With CLEAR_PATIENT_CONTEXT on (certain prod
      // customers) `useCurrentCaseId` can resolve to `NOT_FOUND_CASE_ID` and `buildUrl` then returns a
      // blank route; driving comparative-studies navigation in that state would oscillate case↔blank — a
      // two-URL loop the same-URL guard below cannot break. Priors only make sense for a real case.
      if (caseSmid == null || caseSmid === NOT_FOUND_CASE_ID) return;
      // VX-4394: dedupe priors. `buildUrl` does not dedupe, and the case-sync callers concat the
      // clicked study onto the priors already in the URL, so a repeated lazy-load would otherwise grow
      // the `prior_studies` param (p1 → p1,p1 → …) and defeat the same-URL guard below.
      const uniqueStudySmids = [...new Set(studySmids)];
      const builtUrl = buildUrl({
        caseSmid,
        studySmids: uniqueStudySmids,
        autoloadedStudySmids: autoloadedStudiesIds,
      });

      if (builtUrl == null) return;

      const isSameCase = previousCaseId === caseSmid;

      if (isSameCase) {
        // Preserve unrelated query params (e.g. `hp=create` for the Hanging Protocol form)
        // that the URL builder doesn't manage, so navigation doesn't reset widgets bound to them.
        const preservedParams = new URLSearchParams(searchParams);
        preservedParams.delete(PRIOR_STUDIES_PARAM);
        preservedParams.delete(EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM);
        const preservedQueryString = preservedParams.toString();
        const separator = builtUrl.includes('?') ? '&' : '?';
        const target =
          preservedQueryString.length > 0
            ? `${builtUrl}${separator}${preservedQueryString}`
            : builtUrl;
        // VX-4394: only navigate when the target actually differs from the current location. The
        // case-sync paths (StudiesBar lazy-load, the PRIORS extension round-trip) can re-invoke this
        // with the same case + priors; an unconditional navigate() re-pushes the same URL, whose router
        // state update re-renders and re-triggers the sync — looping until React throws #185 (max update
        // depth). Compared order-insensitively (see normalizeUrlForCompare) so a reordered-but-identical
        // URL can't slip a navigate through. Mirrors the guard already on the cross-case branch below.
        if (
          normalizeUrlForCompare(target) !==
          normalizeUrlForCompare(`${window.location.pathname}${window.location.search}`)
        ) {
          navigate(target);
        }
        return;
      }

      if (shouldRefreshViewer && matchViewer) {
        clearProviderMap();
        window.location.href = builtUrl;
      } else if (window.location.pathname !== builtUrl) {
        clearProviderMap();
        navigate(builtUrl);
      }
    },
    [
      currentCaseId,
      buildUrl,
      autoloadedStudiesIds,
      previousCaseId,
      navigate,
      shouldRefreshViewer,
      matchViewer,
      searchParams,
    ]
  );

  return [priorStudiesIds, setCurrentComparativeStudies];
};

export const useCurrentCaseId = (): Nullable<string> => {
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);

  // Highest priority as it has less chances of being stale
  const viewerUrlWorklistId = useMatch(AppRoutePath.VIEWER)?.params?.worklistId;
  const reporterUrlWorklistId = useMatch({ path: AppRoutePath.REPORTER, end: false })?.params
    ?.worklistId;
  const patientJacketUrlWorklistId = useMatch(AppRoutePath.PATIENT_JACKET)?.params?.smid;
  const patientJacketUndockedUrlWorklistId = useMatch(AppRoutePath.PATIENT_JACKET_UNDOCKED)?.params
    ?.smid;
  const wardWorklistId = getWARDCaseId();

  // Right after the url, as it may have a small delay and result stale
  const { tabs } = useOpenTabs();
  const worklistIds = tabs.map((tab) => tab.worklistIds).flat(1);
  const currentCaseID =
    viewerUrlWorklistId ??
    reporterUrlWorklistId ??
    patientJacketUrlWorklistId ??
    patientJacketUndockedUrlWorklistId ??
    wardWorklistId ??
    worklistIds[0];

  const { data } = useQuery<
    GetLastViewedWorklistItemSmidsQuery,
    GetLastViewedWorklistItemSmidsQueryVariables
  >(GET_LAST_VIEWED_WORKLIST_ITEM_SMIDS, {
    skip: currentCaseID != null || isClearPatientContextEnabled,
    context: { telemetry: { component: 'useCurrentCaseId' } },
  });

  if (isClearPatientContextEnabled) {
    return currentCaseID ?? NOT_FOUND_CASE_ID;
  }

  if (currentCaseID == null) {
    const lastViewedWorklistItemSmids = data?.me?.lastViewedWorklistItemSmids ?? [];
    return lastViewedWorklistItemSmids.length > 0 ? lastViewedWorklistItemSmids[0] : null;
  }

  return currentCaseID;
};

/** Builds and navigates to the URL with the caseId */
export const useSetCurrentCase = (): ((smid: string) => unknown) => {
  const navigate = useNavigate();
  const buildUrl = useUrlBuilder({ isActive: true });
  const [shouldRefreshViewer] = useFeatureFlagEnabled(FF.refresh_viewer_on_new_case);
  const matchViewer = useViewerMatch();

  return useCallback(
    (caseSmid: string) => {
      if (caseSmid == null || caseSmid === NOT_FOUND_CASE_ID) return;
      const url = buildUrl({ caseSmid });
      if (url == null) return;
      if (window.location.pathname !== url) {
        clearProviderMap();
        if (shouldRefreshViewer && matchViewer) {
          saveWindowSyncInfo(caseSmid);
          window.location.href = url;
        } else {
          navigate(url);
          saveWindowSyncInfo(caseSmid);
        }
      }
    },
    [buildUrl, matchViewer, navigate, shouldRefreshViewer]
  );
};

export const useCurrentCase = (): HookResult => {
  const currentCaseId = useCurrentCaseId();
  const {
    data: caseData,
    loading: loadingCase,
    refetch,
  } = useQuery<GetWorklistItemQuery, GetWorklistItemQueryVariables>(GET_WORKLIST_ITEM, {
    variables: currentCaseId == null ? undefined : { smid: currentCaseId },
    skip: currentCaseId == null || currentCaseId === NOT_FOUND_CASE_ID,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    context: {
      telemetry: {
        component: 'useCurrentCase',
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  const refetchWorklistItem = useCallback(async () => {
    if (currentCaseId != null) {
      logger.info('[Current Case]: refetching case');

      await refetch({ smid: currentCaseId });
    }
  }, [currentCaseId, refetch]);

  const prevData = useRef(caseData);
  if (caseData?.worklistItem?.smid !== prevData.current?.worklistItem?.smid) {
    prevData.current = caseData;
  }
  const currentCase = (caseData || prevData.current)?.worklistItem;

  const refreshCase = useAsyncGlobalAction('use-current-case', refetchWorklistItem);

  return {
    currentCaseId,
    currentCase,
    refreshCase,
    loadingCase: loadingCase && currentCase?.smid !== currentCaseId,
  };
};
